import { useState, useEffect, useCallback } from "react";
import { rpcCall, subscribe } from "../ws/client";
import "../styles/PoolChart.scss";

interface ChartDataPoint {
    timestamp: string;
    rate: number;
    ton_balance: number;
    jetton_balance: number;
}

interface PoolChartProps {
    jettonAddress: string;
    jettonSymbol: string;
}

type Timeframe = "1h" | "24h" | "7d" | "30d";

const TIMEFRAMES: { value: Timeframe; label: string; hours: number }[] = [
    { value: "1h", label: "1Ч", hours: 1 },
    { value: "24h", label: "1Д", hours: 24 },
    { value: "7d", label: "7Д", hours: 168 },
    { value: "30d", label: "30Д", hours: 720 }
];

export default function PoolChart({ jettonAddress, jettonSymbol }: PoolChartProps) {
    const [timeframe, setTimeframe] = useState<Timeframe>("24h");
    const [data, setData] = useState<ChartDataPoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadHistory = useCallback(async () => {
        if (!jettonAddress) return;

        setLoading(true);
        setError(null);

        try {
            const hours = TIMEFRAMES.find(tf => tf.value === timeframe)?.hours || 24;
            
            const response = await rpcCall<
                { jetton_address: string; hours: number },
                { history: ChartDataPoint[] }
            >("pool.rate_history", {
                jetton_address: jettonAddress,
                hours
            });

            if (response.result?.history) {
                setData(response.result.history);
            } else if (response.error) {
                setError(response.error.message);
            }
        } catch (err: any) {
            console.error("Failed to load rate history:", err);
            setError(err.message || "Ошибка загрузки данных");
        } finally {
            setLoading(false);
        }
    }, [jettonAddress, timeframe]);

    useEffect(() => {
        loadHistory();
        
        // Subscribe to pool updates
        const unsubscribe = subscribe("pool.updated", () => {
            loadHistory();
        });
        
        return () => unsubscribe();
    }, [loadHistory]);

    // Calculate chart dimensions and data for rendering
    const getChartData = () => {
        if (!data || data.length === 0) return null;

        const rates = data.map(d => d.rate);
        const minRate = Math.min(...rates);
        const maxRate = Math.max(...rates);
        const range = maxRate - minRate;
        const padding = range * 0.1;

        return {
            data,
            minRate: minRate - padding,
            maxRate: maxRate + padding,
            range: range + (2 * padding)
        };
    };

    const formatTimestamp = (timestamp: string, timeframe: Timeframe) => {
        const date = new Date(timestamp);
        
        if (timeframe === "1h") {
            return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
        } else if (timeframe === "24h") {
            return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
        } else {
            return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
        }
    };

    const formatRate = (rate: number) => {
        if (rate < 0.00001) return rate.toExponential(2);
        if (rate < 0.01) return rate.toFixed(6);
        return rate.toFixed(4);
    };

    const chartData = getChartData();

    // Calculate statistics
    const stats = chartData ? (() => {
        const currentRate = data[data.length - 1]?.rate || 0;
        const firstRate = data[0]?.rate || 0;
        const change = currentRate - firstRate;
        const changePercent = firstRate ? ((change / firstRate) * 100) : 0;

        return {
            current: currentRate,
            change,
            changePercent,
            min: chartData.minRate + (chartData.range * 0.1),
            max: chartData.maxRate - (chartData.range * 0.1)
        };
    })() : null;

    return (
        <div className="pool-chart">
            <div className="chart-header">
                <div className="chart-title">
                    <h3>📈 График курса {jettonSymbol}</h3>
                    {stats && (
                        <div className="chart-stats">
                            <span className="current-rate">{formatRate(stats.current)} TON</span>
                            <span className={`rate-change ${stats.change >= 0 ? 'positive' : 'negative'}`}>
                                {stats.change >= 0 ? '+' : ''}{formatRate(stats.change)} ({stats.changePercent.toFixed(2)}%)
                            </span>
                        </div>
                    )}
                </div>
                
                <div className="timeframe-selector">
                    {TIMEFRAMES.map(tf => (
                        <button
                            key={tf.value}
                            className={`timeframe-btn ${timeframe === tf.value ? 'active' : ''}`}
                            onClick={() => setTimeframe(tf.value)}
                            disabled={loading}
                        >
                            {tf.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="chart-error">
                    ⚠️ {error}
                </div>
            )}

            {loading && !data.length && (
                <div className="chart-loading">
                    <div className="spinner"></div>
                    <span>Загрузка данных...</span>
                </div>
            )}

            {!loading && !error && data.length === 0 && (
                <div className="chart-empty">
                    📊 Нет данных для отображения
                </div>
            )}

            {chartData && data.length > 0 && (
                <div className="chart-container">
                    <svg className="chart-svg" viewBox="0 0 800 300" preserveAspectRatio="none">
                        {/* Grid lines */}
                        <g className="grid-lines">
                            {[0, 1, 2, 3, 4].map(i => {
                                const y = (i / 4) * 300;
                                return (
                                    <line
                                        key={i}
                                        x1="0"
                                        y1={y}
                                        x2="800"
                                        y2={y}
                                        stroke="rgba(255, 255, 255, 0.05)"
                                        strokeWidth="1"
                                    />
                                );
                            })}
                        </g>

                        {/* Chart line */}
                        <polyline
                            className="chart-line"
                            fill="none"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            points={data.map((point, i) => {
                                const x = (i / (data.length - 1)) * 800;
                                const normalizedRate = (point.rate - chartData.minRate) / chartData.range;
                                const y = 300 - (normalizedRate * 300);
                                return `${x},${y}`;
                            }).join(' ')}
                        />

                        {/* Area under curve */}
                        <polygon
                            className="chart-area"
                            fill="url(#areaGradient)"
                            points={
                                data.map((point, i) => {
                                    const x = (i / (data.length - 1)) * 800;
                                    const normalizedRate = (point.rate - chartData.minRate) / chartData.range;
                                    const y = 300 - (normalizedRate * 300);
                                    return `${x},${y}`;
                                }).join(' ') + ` 800,300 0,300`
                            }
                        />

                        {/* Gradients */}
                        <defs>
                            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#00ffa3" />
                                <stop offset="100%" stopColor="#00a8ff" />
                            </linearGradient>
                            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="rgba(0, 255, 163, 0.2)" />
                                <stop offset="100%" stopColor="rgba(0, 255, 163, 0)" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Axis labels */}
                    <div className="chart-labels">
                        <div className="y-labels">
                            {[0, 1, 2, 3, 4].map(i => {
                                const rate = chartData.maxRate - (i / 4) * chartData.range;
                                return (
                                    <span key={i} className="y-label">
                                        {formatRate(rate)}
                                    </span>
                                );
                            })}
                        </div>
                        <div className="x-labels">
                            {[0, Math.floor(data.length / 2), data.length - 1].map(i => {
                                if (i >= data.length) return null;
                                return (
                                    <span key={i} className="x-label">
                                        {formatTimestamp(data[i].timestamp, timeframe)}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {stats && !loading && (
                <div className="chart-summary">
                    <div className="summary-item">
                        <span className="summary-label">Мин:</span>
                        <span className="summary-value">{formatRate(stats.min)} TON</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Макс:</span>
                        <span className="summary-value">{formatRate(stats.max)} TON</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Изменение:</span>
                        <span className={`summary-value ${stats.change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.change >= 0 ? '+' : ''}{formatRate(stats.change)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
